import Table from "atoms/Table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import queryKeys from "common/queryKey";
import moment from "moment";
import { useScrollFetch2 } from "hooks/useScrollFetch2";
import { DatePicker, Select, message } from "antd";
import { getMySqlDate } from "utils/Date";
import { connect } from "react-redux";
import { getUserList } from "redux/selectors";
import { concat, find, reduce } from "lodash";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import { CSVLink } from "react-csv";
import ViewMore from "organisms/ViewMore";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const StyledSelect = styled(Select)`
  width: 200px;
`;

const StyledButton = styled(ButtonOutlined)`
  width: auto;

  @media (max-width: 320px) {
    width: 90%;
  }
  @media (max-width: 375px) {
    margin-top: -2px !important;
  }
  @media (max-width: 425px) {
    margin-top: -2px !important;
  }
`;

const { Option } = Select;
const { RangePicker } = DatePicker;

function ActivityLog({ userList }) {
  const { t } = useTranslation();

  const resourceColumns = [
    {
      title: t("Action Type"),
      dataIndex: "actionType",
      key: "actionType",
    },
    {
      title: t("Action Date"),
      dataIndex: "actionDate",
      key: "actionDate",
      render: (date) => moment.utc(date).format("DD-MM-YYYY / HH:mm:ss"),
    },
    {
      title: t("Action By"),
      dataIndex: "userName",
      key: "userName",
      render: (_, record) => {
        const user = find(userList, { userid: record?.actionBy });
        return record?.actionBy && record?.userType === "Owner"
          ? record?.userName
          : user
          ? user?.fullname
          : "-";
      },
    },
    {
      title: t("Logs"),
      dataIndex: "actionLog",
      key: "actionLog",
      width: "40%",
      render: (actionLog) => <ViewMore value={actionLog} count={40} />,
    },
  ];

  const CSVHeaders = [
    { label: t("Action Type"), key: "Action Type" },
    { label: t("Date"), key: "Date" },
    { label: t("Name"), key: "Name" },
    { label: t("Action Log"), key: "Action Log" },
  ];

  const csvLinkEl = useRef();
  const [selectedDateRange, setSelectedDateRange] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [queryParams, setQueryParams] = useState("");
  const [CSVreportlist, setCSVreportlist] = useState([]);

  const handleDateChange = (date) => {
    setSelectedDateRange(date);
  };

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
  };

  const { getActivityLog } = queryKeys;
  const { key, url } = getActivityLog(queryParams);

  const { data, lastElementRef, isLoading } = useScrollFetch2(key, {
    endPoint: url,
    queryParam: "lastEvaluatedKey",
  });

  const activitylog = useMemo(() => {
    return data?.pages?.reduce((acc, page) => {
      if (!page?.response?.data) return acc;
      return [...acc, ...page.response.data];
    }, []);
  }, [data]);

  const getQueryParams = () => {
    let startDate, endDate;
    if (selectedDateRange) {
      [startDate, endDate] = selectedDateRange;
    }

    const queryParams = new URLSearchParams();
    queryParams.append("actionType", selectedFilter ? selectedFilter : 0);

    if (startDate && endDate) {
      queryParams.append("startDate", getMySqlDate(startDate));
      queryParams.append("endDate", getMySqlDate(endDate));
    } else {
      queryParams.append("startDate", 0);
      queryParams.append("endDate", 0);
    }

    queryParams.append("actionBy", selectedUser ? selectedUser : 0);
    setQueryParams(`?${queryParams.toString()}`);
  };

  useEffect(() => {
    getQueryParams();
  }, [selectedFilter, selectedDateRange, selectedUser]);

  const prepareCSV = async () => {
    if (activitylog.length > 0) {
      setCSVreportlist(
        reduce(
          activitylog,
          (result, record) => {
            const user = find(userList, {
              userid: record?.actionBy,
            });

            result.push({
              "Action Type": record?.actionType,
              Date: moment
                .utc(record.actionDate)
                .format("DD-MM-YYYY / HH:mm:ss"),
              Name:
                record?.actionBy && record?.userType === "Owner"
                  ? record?.userName
                  : user
                  ? user?.fullname
                  : "-",
              "Action Log": record.actionLog,
            });

            return result;
          },
          []
        )
      );

      setTimeout(() => {
        csvLinkEl.current.link.click();
      });
    } else {
      message.error(t("No data available to download."));
    }
  };

  return (
    <div>
      <div className="w-100 d-flex justify-content-between">
        <h5>{t("Activity Log")}</h5>

        <div className="text-end">
          <StyledButton
            onClick={prepareCSV}
            disabled={activitylog?.length === 0}
          >
            {t("Export CSV file")}
          </StyledButton>

          <CSVLink
            headers={CSVHeaders}
            data={CSVreportlist}
            filename={"activityLog.csv"}
            asyncOnClick
            ref={csvLinkEl}
          />
        </div>
      </div>

      <div className="w-100 d-flex gap-2 align-itmes-center flex-wrap mb-2">
        <StyledSelect
          placeholder={t("Select Action Type")}
          onChange={handleFilterChange}
        >
          <Option value="">{t("All")}</Option>
          <Option value="Edited">{t("Edited")}</Option>
          <Option value="Created">{t("Created")}</Option>
          <Option value="Deleted">{t("Deleted")}</Option>
        </StyledSelect>

        <StyledSelect
          placeholder={t("Filter by Employee")}
          options={concat(
            {
              userid: "",
              fullname: t("All Employee"),
            },
            userList
          )}
          fieldNames={{
            label: "fullname",
            value: "userid",
          }}
          onChange={(val) => setSelectedUser(val)}
        />

        <RangePicker
          value={selectedDateRange}
          onChange={handleDateChange}
          format="DD-MM-YYYY"
          style={{ width: "200px" }}
          allowClear
          placeholder={[t("Start Date"), t("End Date")]}
        />
      </div>

      <Table
        bordered={false}
        className="mb-5"
        dataSource={activitylog}
        columns={resourceColumns}
        pagination={false}
        loading={isLoading}
        onRow={(record, index) => ({
          ref: index === activitylog.length - 30 ? lastElementRef : null,
        })}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  userList: getUserList(state),
});

export default connect(mapStateToProps, null)(ActivityLog);
